import { getPasswordManagerAttributes, Icon, Separator } from '@amelio/ui-library';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { TranslationKey } from '@/i18n-key';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';

import { TranslationField } from '@/components/TranslationField';
import { TranslationLanguagePicker } from '@/components/TranslationField/TranslationLanguagePicker';

const DEFAULT_NAME_MAX_LENGTH = 50;
const DEFAULT_DESCRIPTION_MAX_LENGTH = 300;
const DEFAULT_NAME_LABEL: TranslationKey = 'visionValues.nameLabel';
const DEFAULT_DESCRIPTION_LABEL: TranslationKey = 'visionValues.descriptionLabel';
const DEFAULT_NAME_MAX_LENGTH_MESSAGE: TranslationKey = 'visionValues.nameMaxLength';
const DEFAULT_DESCRIPTION_MAX_LENGTH_MESSAGE: TranslationKey = 'visionValues.descriptionMaxLength';

interface ValueTranslationsTabProps {
    valueId: string | null;
    questions: ReviewQuestion[];
    configuredLanguages: string[];
    currentLanguage: string;
    initialTranslations: Record<string, Record<string, string>>;
    onTranslationsChange: (translations: Record<string, Record<string, string>>) => void;
    onValidationChange?: (isValid: boolean) => void;
    /** Lets a consumer other than company values relabel and re-bound the two fields. */
    nameLabel?: TranslationKey;
    descriptionLabel?: TranslationKey;
    nameMaxLength?: number;
    descriptionMaxLength?: number;
    /** The over-limit messages naming what is too long, in the consumer's own wording. */
    nameMaxLengthMessage?: TranslationKey;
    descriptionMaxLengthMessage?: TranslationKey;
    /** `false` for an entity that has no description at all (objective periods). @default true */
    showDescription?: boolean;
}

export default function ValueTranslationsTab({
    questions,
    configuredLanguages,
    currentLanguage: editingLanguage,
    initialTranslations,
    onTranslationsChange,
    onValidationChange,
    nameLabel = DEFAULT_NAME_LABEL,
    descriptionLabel = DEFAULT_DESCRIPTION_LABEL,
    nameMaxLength = DEFAULT_NAME_MAX_LENGTH,
    descriptionMaxLength = DEFAULT_DESCRIPTION_MAX_LENGTH,
    nameMaxLengthMessage = DEFAULT_NAME_MAX_LENGTH_MESSAGE,
    descriptionMaxLengthMessage = DEFAULT_DESCRIPTION_MAX_LENGTH_MESSAGE,
    showDescription = true,
}: ValueTranslationsTabProps) {
    const { t } = useTranslation();
    const [translations, setTranslations] =
        useState<Record<string, Record<string, string>>>(initialTranslations);
    const [selectedLanguage, setSelectedLanguage] = useState<string | null>(
        configuredLanguages.find((lang) => lang !== editingLanguage) || null,
    );

    useEffect(() => {
        setTranslations(initialTranslations);
    }, [initialTranslations]);

    const additionalLanguages = useMemo(
        () => configuredLanguages.filter((lang) => lang !== editingLanguage),
        [configuredLanguages, editingLanguage],
    );

    // Set selected language when available languages change
    useEffect(() => {
        if (!selectedLanguage && additionalLanguages.length > 0) {
            setSelectedLanguage(additionalLanguages[0]);
        }
    }, [additionalLanguages, selectedLanguage]);

    // Validate all translations and notify parent
    useEffect(() => {
        if (!onValidationChange) return;
        const isValid = Object.values(translations).every((langTrans) => {
            const nameValid = !langTrans.name || langTrans.name.length <= nameMaxLength;
            const descValid =
                !langTrans.description || langTrans.description.length <= descriptionMaxLength;
            return nameValid && descValid;
        });
        onValidationChange(isValid);
    }, [translations, onValidationChange, nameMaxLength, descriptionMaxLength]);

    const handleTranslationChange = (
        language: string,
        field: 'name' | 'description',
        value: string,
    ) => {
        setTranslations((prev) => {
            const updated = { ...prev };
            if (!updated[language]) {
                updated[language] = {};
            }
            updated[language][field] = value;
            onTranslationsChange(updated);
            return updated;
        });
    };

    const handleQuestionTranslationChange = (
        language: string,
        questionId: string,
        value: string,
    ) => {
        setTranslations((prev) => {
            const updated = { ...prev };
            if (!updated[language]) {
                updated[language] = {};
            }
            updated[language][`question_${questionId}`] = value;
            onTranslationsChange(updated);
            return updated;
        });
    };

    const handleOptionTranslationChange = (language: string, optionId: string, value: string) => {
        setTranslations((prev) => {
            const updated = { ...prev };
            if (!updated[language]) {
                updated[language] = {};
            }
            updated[language][`option_${optionId}`] = value;
            onTranslationsChange(updated);
            return updated;
        });
    };

    const isLanguageComplete = (language: string) => {
        const langTrans = translations[language];
        if (!langTrans) return false;
        // Check if name and description are filled
        const hasName = !!langTrans.name;
        // A consumer without a description field can never fill one in: requiring it would leave
        // every language permanently marked incomplete.
        const hasDescription = !showDescription || !!langTrans.description;
        // Check if all questions have translations
        const allQuestionsTranslated = questions.every((q) => !!langTrans[`question_${q.id}`]);
        // Check if all options have translations
        const allOptionsTranslated = questions.every((q) => {
            if (!q.reviewQuestionOptions || q.reviewQuestionOptions.length === 0) {
                return true; // No options to translate
            }
            return q.reviewQuestionOptions.every((opt) => !!langTrans[`option_${opt.id}`]);
        });
        return hasName && hasDescription && allQuestionsTranslated && allOptionsTranslated;
    };

    if (additionalLanguages.length === 0) {
        return (
            <div className={'flex flex-col items-center justify-center py-12 text-gray-500'}>
                <Icon
                    name={'location-global'}
                    size={'lg'}
                    colorClassName={'text-gray-400'}
                />

                <p className={'text-subtitle mt-4 font-medium'}>
                    {t('visionValues.noAdditionalLanguages')}
                </p>

                <p className={'text-body-sm'}>{t('visionValues.addEvaluationLanguagesHint')}</p>
            </div>
        );
    }

    const currentLanguage = selectedLanguage || additionalLanguages[0];
    const langTrans = translations[currentLanguage] || {};

    if (!selectedLanguage) {
        return null;
    }

    return (
        <TranslationLanguagePicker
            languages={additionalLanguages}
            selectedLanguage={currentLanguage}
            onSelectLanguage={setSelectedLanguage}
            isLanguageComplete={isLanguageComplete}
        >
            {/* Name Translation */}
            <TranslationField
                label={t(nameLabel)}
                value={langTrans.name || ''}
                onChange={(value) => handleTranslationChange(currentLanguage, 'name', value)}
                maxLength={nameMaxLength}
                placeholder={t('visionValues.translationMissingFallback')}
                overLimitMessage={t(nameMaxLengthMessage, { max: nameMaxLength })}
                missingTranslationLabel={t('visionValues.missingTranslation')}
            />

            {/* Description Translation */}
            {showDescription && (
                <TranslationField
                    label={t(descriptionLabel)}
                    value={langTrans.description || ''}
                    onChange={(value) =>
                        handleTranslationChange(currentLanguage, 'description', value)
                    }
                    maxLength={descriptionMaxLength}
                    placeholder={t('visionValues.translationMissingFallback')}
                    overLimitMessage={t(descriptionMaxLengthMessage, {
                        max: descriptionMaxLength,
                    })}
                    missingTranslationLabel={t('visionValues.missingTranslation')}
                    multiline
                />
            )}

            {/* Question Translations */}
            {questions.length > 0 && (
                <div className={'flex flex-col gap-4'}>
                    <Separator
                        thickness={'thin'}
                        className={'!my-3 !mb-5 bg-gray-200'}
                    />
                    <h4 className={'text-navy text-body-sm font-semibold uppercase'}>
                        {t('visionValues.questionTexts')}
                    </h4>

                    {questions.map((question) => {
                        const questionLabel =
                            question.label[editingLanguage] ||
                            Object.values(question.label)[0] ||
                            '';
                        const truncatedLabel =
                            questionLabel.length > 50
                                ? questionLabel.substring(0, 50) + '...'
                                : questionLabel;

                        return (
                            <div
                                key={question.id}
                                className={'bg-base-25 flex flex-col gap-2 rounded-xl p-4'}
                            >
                                <label className={'text-navy text-xs font-medium text-gray-600'}>
                                    &quot;{truncatedLabel}&quot;
                                </label>

                                <input
                                    {...getPasswordManagerAttributes()}
                                    type={'text'}
                                    value={langTrans[`question_${question.id}`] || ''}
                                    onChange={(e) =>
                                        handleQuestionTranslationChange(
                                            currentLanguage,
                                            question.id!,
                                            e.target.value,
                                        )
                                    }
                                    placeholder={t('visionValues.translationMissingFallback')}
                                    className={
                                        'text-navy focus:ring-primary/50 h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm focus:ring-2 focus:outline-none'
                                    }
                                />

                                {!langTrans[`question_${question.id}`] && (
                                    <div className={'flex items-center gap-1'}>
                                        <Icon
                                            name={'essential-info-circle'}
                                            size={'xs'}
                                            colorClassName={'text-amber-700'}
                                        />
                                        <span className={'text-xs text-amber-700'}>
                                            {t('visionValues.missingTranslation')}
                                        </span>
                                    </div>
                                )}

                                {/* Option translations for questions with options */}
                                {question.reviewQuestionOptions &&
                                    question.reviewQuestionOptions.length > 0 && (
                                        <div className={'mt-3 flex flex-col gap-2'}>
                                            <h5 className={'text-navy text-caption font-semibold'}>
                                                Options
                                            </h5>
                                            {question.reviewQuestionOptions.map((option) => {
                                                const optionLabel =
                                                    option.label[editingLanguage] ||
                                                    Object.values(option.label)[0] ||
                                                    '';
                                                const truncatedLabel =
                                                    optionLabel.length > 40
                                                        ? optionLabel.substring(0, 40) + '...'
                                                        : optionLabel;

                                                return (
                                                    <div
                                                        key={option.id}
                                                        className={'ml-4 flex flex-col gap-1'}
                                                    >
                                                        <label
                                                            className={
                                                                'text-navy text-xs font-medium text-gray-500'
                                                            }
                                                        >
                                                            &quot;{truncatedLabel}&quot;
                                                        </label>
                                                        <input
                                                            {...getPasswordManagerAttributes()}
                                                            type={'text'}
                                                            value={
                                                                langTrans[`option_${option.id}`] ||
                                                                ''
                                                            }
                                                            onChange={(e) =>
                                                                handleOptionTranslationChange(
                                                                    currentLanguage,
                                                                    option.id,
                                                                    e.target.value,
                                                                )
                                                            }
                                                            placeholder={
                                                                'Translation missing — will fall back to default language'
                                                            }
                                                            className={
                                                                'text-navy focus:ring-primary/50 h-9 rounded-lg border border-gray-300 bg-white px-3 text-sm focus:ring-2 focus:outline-none'
                                                            }
                                                        />
                                                        {!langTrans[`option_${option.id}`] && (
                                                            <div
                                                                className={
                                                                    'flex items-center gap-1'
                                                                }
                                                            >
                                                                <Icon
                                                                    name={'essential-info-circle'}
                                                                    size={'xs'}
                                                                    colorClassName={
                                                                        'text-amber-600'
                                                                    }
                                                                />
                                                                <span
                                                                    className={
                                                                        'text-xs text-amber-700'
                                                                    }
                                                                >
                                                                    {t(
                                                                        'visionValues.missingTranslation',
                                                                    )}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                            </div>
                        );
                    })}
                </div>
            )}
        </TranslationLanguagePicker>
    );
}
